import {
  Add,
  Business,
  ContentCopy,
  Delete,
  Edit,
  MoreVert,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { SolaForceTabs } from '~/components/SolaForceTabs';
import { AdminWorkspaceRoles } from '~/data/user/role';
import { AdminModeToggle } from '~/scenes/MyWorkspace/components/WorkspaceToolbar/components/WorkspaceToolbarReadMode/components/AdminModeToggle';
import { useWorkspaceSetupDialog } from '~/scenes/MyWorkspace/components/WorkspaceToolbar/components/WorkspaceToolbarReadMode/components/WorkspaceSetupDialog/useWorkspaceSetupDialog';
import {
  WorkspaceType,
  getWorkspaceTypeTranslationKey,
  getWorkspaceUserGroup,
  getWorkspaceUserGroupTranslationKey,
} from '~/scenes/MyWorkspace/data';

import { WorkspaceRoleIcon } from '../WorkspaceRoleIcon';

import { WorkspaceSetupDialog } from './components/WorkspaceSetupDialog';
import { useSxStyles } from './useStyles';
import { useWorkspaceToolbarReadMode } from './useWorkspaceToolbarReadMode';

export const WorkspaceToolbarReadMode = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    dialogOpen,
    handleDialogOpen,
    handleDialogClose,
    handleDialogSubmit,
    formSettings,
  } = useWorkspaceSetupDialog();

  const {
    menuAnchorEl,
    handleMenuOpen,
    handleMenuClose,
    workspaces,
    activeWorkspace,
    handleCreateWorkspace,
    handleManageWorkspace,
    handleDuplicateWorkspace,
    handleActiveWorkspaceChange,
    handleActiveWorkspaceDelete,
    canEditWorkspace,
    tabsWorkspaces,
    adminMode,
  } = useWorkspaceToolbarReadMode({ handleSetupDialogOpen: handleDialogOpen });

  const hasWorkspaces = workspaces.length > 0;
  const order = {
    [AdminWorkspaceRoles.EMPLOYEE]: 0,
    [AdminWorkspaceRoles.MANAGER]: 1,
    [AdminWorkspaceRoles.HR_ADMIN]: 2,
    [AdminWorkspaceRoles.HR_STAFF]: 3,
    [AdminWorkspaceRoles.ALT_MANAGER]: 4,
    [AdminWorkspaceRoles.DEPUTY_MANAGER]: 5,
  };

  const orderedWorkspaces = tabsWorkspaces.sort((a, b) => {
    return order[a.restrictToRoles[0]] - order[b.restrictToRoles[0]];
  });

  return (
    <Box sx={sxStyles.grid}>
      <Box sx={sxStyles.tabsArea}>
        <SolaForceTabs
          value={activeWorkspace.id}
          onChange={(e, v) => handleActiveWorkspaceChange(v)}
          background={'transparent'}
          shadow={'none'}
        >
          {orderedWorkspaces.map(({ id, name, type, restrictToRoles }) => {
            const isCompanyWorkspace = type === WorkspaceType.Company;

            const typeLabel = t(getWorkspaceTypeTranslationKey(type));
            const userGroupLabel = t(
              getWorkspaceUserGroupTranslationKey(
                getWorkspaceUserGroup(restrictToRoles[0]),
              ),
            );
            const getIconLabel = (label: string) =>
              `${t('workspaceType')}: ${label}`;

            return (
              <Tab
                key={id}
                icon={
                  <Badge
                    overlap="circular"
                    badgeContent={
                      isCompanyWorkspace ? (
                        <Business
                          fontSize={'inherit'}
                          className={'workspace-type-icon'}
                        />
                      ) : undefined
                    }
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    sx={{ '& .MuiBadge-badge': { fontSize: '1rem' } }}
                    title={getIconLabel(`${typeLabel} - ${userGroupLabel}`)}
                  >
                    <WorkspaceRoleIcon
                      type={restrictToRoles[0]}
                      sx={{
                        verticalAlign: 'text-top',
                      }}
                      fontSize={'medium'}
                    />
                  </Badge>
                }
                iconPosition={'start'}
                sx={sxStyles.tab}
                label={name}
                value={id}
              />
            );
          })}
        </SolaForceTabs>
      </Box>
      <Box sx={sxStyles.addNewArea}>
        <IconButton
          aria-label={t('createWorkspace')}
          onClick={handleCreateWorkspace}
          size="large"
          title={t('createWorkspace')}
        >
          <Add />
        </IconButton>

        {adminMode.visible && (
          <AdminModeToggle
            onToggle={adminMode.toggle}
            checked={adminMode.selected}
            disabled={!adminMode.enabled}
          />
        )}
      </Box>
      {hasWorkspaces && (
        <Box sx={sxStyles.actionsMenuArea}>
          <IconButton
            aria-controls="manage-workspaces-menu"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            data-cy="manage-workspaces-menu-icon"
            size="large"
            title={t('workspaceActions')}
          >
            <MoreVert />
          </IconButton>

          <Menu
            id="manage-workspaces-menu"
            anchorEl={menuAnchorEl}
            open={!!menuAnchorEl}
            onClose={handleMenuClose}
            data-cy="manage-workspaces-menu"
          >
            {canEditWorkspace() && (
              <MenuItem
                onClick={handleManageWorkspace}
                data-cy="manage-workspace"
              >
                <ListItemIcon>
                  <Edit />
                </ListItemIcon>
                {t('editWorkspace')}
              </MenuItem>
            )}
            <MenuItem
              onClick={handleDuplicateWorkspace}
              data-cy="duplicate-workspace"
            >
              <ListItemIcon>
                <ContentCopy />
              </ListItemIcon>
              {t('duplicateWorkspace')}
            </MenuItem>
            {canEditWorkspace() && (
              <MenuItem
                onClick={handleActiveWorkspaceDelete}
                data-cy="delete-workspace"
              >
                <ListItemIcon>
                  <Delete />
                </ListItemIcon>
                {t('deleteWorkspace')}
              </MenuItem>
            )}
          </Menu>
        </Box>
      )}
      <WorkspaceSetupDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        formSettings={formSettings}
        workspaces={workspaces}
      />
    </Box>
  );
};
